import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { ArrowIcon } from 'assets';
import { AuthHeading, Button, Input, Spinner } from 'view/components';
import { AuthFormsFooter, Agreement } from 'view/pages/auth/components';
import { useConfirmEmail } from 'view/pages/auth/hooks/useConfirmEmail';
import { useCreateNewPassword } from 'view/pages/auth/hooks/useCreateNewPassword';
import {
  AuthHeadingImageTypeEnums,
  getCommonPasswordValidationRules,
  parseError,
} from 'view/pages/auth/helpers';
import * as CS from 'view/pages/auth/commonStyled';

interface IFormInput {
  create_password: string;
  confirm_password: string;
}

const PasswordFormFields: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  const {
    watch,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormContext<IFormInput>();
  const createPasswordValue = watch('create_password');
  const confirmPasswordValue = watch('confirm_password');

  useEffect(() => {
    if (createPasswordValue || confirmPasswordValue) {
      trigger('create_password').then();
      trigger('confirm_password').then();

      if (createPasswordValue === confirmPasswordValue) {
        clearErrors(['create_password', 'confirm_password']);
      }
    }
  }, [createPasswordValue, confirmPasswordValue, trigger, clearErrors]);

  const isDisabled = !!errors.create_password || !!errors.confirm_password || isLoading;

  return (
    <>
      <Input
        label="Create Password"
        name="create_password"
        type="password"
        rules={{
          ...getCommonPasswordValidationRules('Password'),
          validate: (value: string) => {
            return value === confirmPasswordValue || 'Passwords do not match';
          },
        }}
        parseError={parseError}
      />
      <Input
        name="confirm_password"
        label="Confirm Password"
        type="password"
        rules={{
          ...getCommonPasswordValidationRules('Password'),
          validate: (value: string) => {
            return value === createPasswordValue || 'Passwords do not match';
          },
        }}
        parseError={parseError}
      />
      <CS.FormWrapperMain component="div">
        <Button
          variant="contained"
          type="submit"
          text="Continue"
          endIcon={
            isLoading ? (
              <Spinner disabled={isDisabled} variant="contained" />
            ) : (
              <ArrowIcon rotate="right" color="white" />
            )
          }
          disabled={isDisabled}
        />
        <Agreement />
      </CS.FormWrapperMain>
    </>
  );
};

const CreatePasswordPage = () => {
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);
  const hash = params.get('hash');

  const { send } = useConfirmEmail();
  const { isLoading, createNewPassword } = useCreateNewPassword();

  useEffect(() => {
    if (!hash) {
      navigate('/auth/login', { replace: true });
      return;
    }

    send({ hash });
  }, [hash]);

  const onSubmit = (formData: IFormInput) => {
    createNewPassword({ password: formData.create_password });
  };

  return (
    <CS.Wrapper>
      {/* <CS.BackButtonBox>
        <BackButton text="Back to Authorization" fallbackUrl="/auth/onboarding" />
      </CS.BackButtonBox> */}
      <CS.AuthMainBox component="section">
        <AuthHeading
          title="Create password"
          subTitle={'Email verified. Please create password \n to protect your account'}
          imageType={AuthHeadingImageTypeEnums.LINE}
        />
        <CS.FormWrapper component="section">
          <FormContainer
            defaultValues={{ create_password: '', confirm_password: '' }}
            onSuccess={onSubmit}
          >
            <PasswordFormFields isLoading={isLoading} />
          </FormContainer>
          <AuthFormsFooter
            title="Already have an account? "
            hrefTo="/auth/login"
            hrefText="Log In"
          />
        </CS.FormWrapper>
      </CS.AuthMainBox>
    </CS.Wrapper>
  );
};

export default CreatePasswordPage;
